import React, { useState, useReducer, useRef, useEffect } from 'react'
import {
  TextField
} from '@material-ui/core'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { getLaws } from 'utils/laws/actions'
import useStyles from './styles'
import { formReducer, getFieldError } from 'helpers/forms'
import { getByIdFromArray } from 'helpers/dataHelpers'
import { checkErrorStatus, setSnackbar } from 'utils/common/actions'
import CountriesInput from 'components/CountriesInput'
import Modal from 'components/Modal'
import Config from 'config'

const initFormsState = {
  errors: {}
}


const AddApplicableLaw = (props) => {
  const {
    applicableLawId,
    setShowDialog,
    showDialog,
    getValue,
    lawData,
    countries
  } = props
  const { apiUrl } = Config.network
  const dispatch = useDispatch()
  const [formErrors, dispatchFormErrors] = useReducer(formReducer, initFormsState)
  const [country, setCountry] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const laws = useSelector(state => state.laws.laws)

  useEffect(() => {
    dispatchFormErrors({ type: 'reset' })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getLawData = () => {
    let lawDict = null
    if (applicableLawId) {
      lawDict = getByIdFromArray(laws, applicableLawId)
    }
    if (!lawDict && lawData && lawData.short_name) {
      lawDict = lawData
    }
    return lawDict
  }

  const activeApplicableLaw = getLawData()
  
  const formRef = useRef()

  const localClasses = useStyles()


  const addApplicableLawSubmit = (event) => {
    event.preventDefault()
    event.stopPropagation()
    dispatchFormErrors({ type: 'reset' })
    setIsLoading(true)
    const url = `${apiUrl}laws/`
    const short_name = event.target.elements.short_name.value
    const name = event.target.elements.name.value
    const articles = event.target.elements.articles.value
    const name_en = event.target.elements.name_en.value
    const law_url = event.target.elements.url.value
    const requestData = {
      short_name: short_name,
      articles: articles,
      name: name,
      name_en: name_en,
      url: law_url,
      country: country
    }
    axios.post(url, requestData).then((body) => {
      const { data } = body
      dispatch(setSnackbar('Law created'))
      setIsLoading(false)
      if (getValue) {
        getValue('create', data)
      } else {
        dispatch(getLaws())
      }
      setShowDialog(false)
    }).catch(err => {
      if (err.response) {
        dispatch(checkErrorStatus(err.response.status))
        setIsLoading(false)
        if (err.response.status === 400) {
          dispatchFormErrors({
            type: 'error',
            payload: err.response.data
          })
        } else {
          dispatchFormErrors({
            type: 'error',
            payload: {
              nonField: [err.response.data.detail]
            }
          })
        }
      } else {
        dispatch(setSnackbar('Connection error', 'error'))
      }
    })
  }

  const editApplicableLawSubmit = (event) => {
    event.preventDefault()
    event.stopPropagation()
    dispatchFormErrors({ type: 'reset' })
    setIsLoading(true)
    const url = `${apiUrl}laws/${applicableLawId}/`
    const short_name = event.target.elements.short_name.value
    const name = event.target.elements.name.value
    const articles = event.target.elements.articles.value
    const name_en = event.target.elements.name_en.value
    const law_url = event.target.elements.url.value
    const requestData = {
      short_name: short_name,
      articles: articles,
      name: name,
      name_en: name_en,
      url: law_url,
      country: country
    }
    axios.put(url, requestData).then((body) => {
      const { data } = body
      dispatch(setSnackbar('Law updated'))
      setIsLoading(false)
      if (getValue) {
        getValue('edit', data)
      } else {
        dispatch(getLaws())
      }
      setShowDialog(false)
    }).catch(err => {
      if (err.response) {
        dispatch(checkErrorStatus(err.response.status))
        setIsLoading(false)
        if (err.response.status === 400) {
          dispatchFormErrors({
            type: 'error',
            payload: err.response.data
          })
        } else {
          dispatchFormErrors({
            type: 'error',
            payload: {
              nonField: [err.response.data.detail]
            }
          })
        }
      } else {
        dispatch(setSnackbar('Connection error', 'error'))
      }
    })
  }

  const handleClose = (event) => {
    event.stopPropagation()
    setShowDialog(false)
  }

  return (
    <Modal
      open={showDialog}
      onClose={handleClose}
      header={applicableLawId ? 'Edit Applicable Law' : 'Add Applicable Law'}
      isLoading={isLoading}
      action={() => formRef.current.dispatchEvent(new Event('submit'))}
      actionText={applicableLawId ? 'Save' : 'Create Applicable Law'}
      secondaryAction={handleClose}
    >
      <form onSubmit={applicableLawId ? editApplicableLawSubmit : addApplicableLawSubmit} ref={formRef}>
        { getFieldError(formErrors, 'nonField') ?
        <p className='error-message'>{getFieldError(formErrors, 'nonField')}</p> : null }

          <TextField
            required
            error={!!getFieldError(formErrors, 'short_name')}
            helperText={getFieldError(formErrors, 'short_name')}
            className={localClasses.textField}
            color='secondary'
            placeholder='Short name for the law...'
            label='Short name'
            type='text'
            name='short_name'
            fullWidth
            variant='outlined'
            defaultValue={activeApplicableLaw ? activeApplicableLaw.short_name : null}
            InputLabelProps={{
              shrink: true,
              required: true
            }}
          />

          <TextField
            required
            error={!!getFieldError(formErrors, 'articles')}
            helperText={getFieldError(formErrors, 'articles')}
            className={localClasses.textField}
            color='secondary'
            placeholder='Type articles, example: 1, 5, 8-23, 6 ...'
            label='Articles'
            type='text'
            name='articles'
            fullWidth
            variant='outlined'
            defaultValue={activeApplicableLaw ? activeApplicableLaw.articles : null}
            InputLabelProps={{
              shrink: true,
              required: false
            }}
          />

          <TextField
            error={!!getFieldError(formErrors, 'name')}
            helperText={getFieldError(formErrors, 'name')}
            className={localClasses.textField}
            color='secondary'
            placeholder='Write the long name in original languege...'
            label='Long name - Original language'
            type='text'
            name='name'
            fullWidth
            variant='outlined'
            multiline
            minRows={6}
            maxRows={6}
            defaultValue={activeApplicableLaw ? activeApplicableLaw.name : null}
            InputLabelProps={{
              shrink: true,
              required: true
            }}
          />

          <TextField
            error={!!getFieldError(formErrors, 'name_en')}
            helperText={getFieldError(formErrors, 'name_en')}
            className={localClasses.textField}
            color='secondary'
            placeholder='Write the long name in English...'
            label='Long name - EN'
            type='text'
            name='name_en'
            fullWidth
            variant='outlined'
            multiline
            minRows={6}
            maxRows={6}
            defaultValue={activeApplicableLaw ? activeApplicableLaw.name_en : null}
            InputLabelProps={{
              shrink: true,
              required: true
            }}
          />

          <TextField
            required
            error={!!getFieldError(formErrors, 'url')}
            helperText={getFieldError(formErrors, 'url')}
            className={localClasses.textField}
            color='secondary'
            placeholder='Paste a source link...'
            label='Source link'
            type='url'
            name='url'
            fullWidth
            variant='outlined'
            defaultValue={activeApplicableLaw ? activeApplicableLaw.url : null}
            InputLabelProps={{
              shrink: true,
              required: true
            }}
          />

          <CountriesInput
            error={!!getFieldError(formErrors, 'country')}
            helperText={getFieldError(formErrors, 'country')}
            className={localClasses.textField}
            countryVariants={countries}
            setValue={setCountry}
            color='secondary'
            placeholder='Choose a country...'
            defaultValue={activeApplicableLaw ? activeApplicableLaw.country : null}
            InputLabelProps={{
              shrink: true,
              required: true
            }}
          />

      </form>
    </Modal>
  )
}

export default AddApplicableLaw
